import { kSourceLrrHtmlAgent } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { nanoid } from 'nanoid'
import { getAuth } from 'firebase/auth'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import * as Sentry from '@sentry/browser'
import { ApiError } from '@/util/errors/api-error'
import { AgentReferenceHtmlResponseSchema, AgentReferenceHtmlResponse } from '../store/schemas'

/**
 * Fetch LRR HTML for Agent Reference
 * Uses V3 endpoint with s3_path from reference metadata
 *
 * @param s3_path - The s3 directory path from reference metadata
 * @returns The HTML content for the LRR reference
 */
export async function fetchAgentLrrHtml(s3_path: string): Promise<AgentReferenceHtmlResponse> {
  const sentry_transaction_id = nanoid()
  const token = await getAuth().currentUser?.getIdToken()
  if (!token) throw Error(kErrorCodeAuthError)

  try {
    // Construct API URL with s3_path query parameter
    const apiUrl = kPaxtonAppApiRESTBaseUrl() + kSourceLrrHtmlAgent + '/' + s3_path

    const requestOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentry_transaction_id,
        Authorization: `Bearer ${token}`,
      },
      method: 'GET',
    }

    const response = await fetch(apiUrl, requestOptions)

    // Check response status BEFORE parsing JSON
    if (!response.ok) {
      // Try to parse error details if response is JSON, otherwise use status text
      let errorDetail = response.statusText
      try {
        const errorData = await response.json()
        errorDetail = JSON.stringify(errorData.detail || errorData)
      } catch {
        // Response is not JSON (e.g., HTML error page), use status text
      }

      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })

        Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${errorDetail}`), {
          extra: {
            requestUrl: apiUrl,
            requestOptions: requestOptions,
            responseStatus: response.status,
            responseStatusText: response.statusText,
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })
      throw new ApiError(response.status, `Fetch error - status: ${response.status}, detail: ${errorDetail}`)
    }

    // Parse JSON only if response is OK

    const data = await response.json()

    // Validate response against schema
    const validatedData = AgentReferenceHtmlResponseSchema.parse(data)

    return validatedData
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })

        Sentry.captureException(error, {
          extra: { onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled, s3_path },
        })
      })
    }
    throw error
  }
}
